/**
 * 액션 수행 후 DOM 변경이 안정화될 때까지 기다리고 새로운 컨텍스트를 반환
 * debounce 방식으로 2초 동안 DOM 변경이 없을 때까지 대기
 */
export const performElementActionAndWait = async (
  selector: string | undefined, // sendKeys일 때는 옵셔널 (현재 포커스된 요소 사용)
  action: 'click' | 'focus' | 'scroll' | 'input' | 'doubleClick' | 'sendKeys',
  value?: string,
): Promise<{ success: boolean; newContext?: any }> => {
  const success = performElementAction(selector, action, value);

  if (success) {
    return new Promise((resolve) => {
      waitForDOMStabilization(() => {
        resolve({ success: true });
      });
    });
  } else {
    return { success: false };
  }
};

/**
 * DOM 변경이 2초 동안 없을 때까지 기다리는 함수 (debounce 방식)
 */
const waitForDOMStabilization = (callback: () => void, timeout: number = 2000) => {
  let debounceTimer: any = null;
  let observer: MutationObserver | null = null;

  const cleanup = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  const resetTimer = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      cleanup();
      callback();
    }, timeout);
  };

  // MutationObserver로 DOM 변경 감지
  observer = new MutationObserver((mutations) => {
    let hasSignificantChange = false;

    mutations.forEach((mutation) => {
      // 중요한 변경사항만 감지 (텍스트 변경, 요소 추가/제거, 속성 변경)
      if (
        mutation.type === 'childList' &&
        (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)
      ) {
        hasSignificantChange = true;
      } else if (mutation.type === 'attributes') {
        // 중요한 속성 변경만 감지
        const importantAttributes = ['class', 'style', 'disabled', 'hidden'];
        const attributeName = mutation.attributeName || '';
        if (
          importantAttributes.indexOf(attributeName) !== -1 ||
          attributeName.indexOf('data-') === 0
        ) {
          hasSignificantChange = true;
        }
      } else if (mutation.type === 'characterData') {
        hasSignificantChange = true;
      }
    });

    if (hasSignificantChange) {
      console.log('DOM 변경 감지, 타이머 리셋');
      resetTimer();
    }
  });

  // DOM 전체를 관찰 (하위 트리 포함)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeOldValue: true,
    characterData: true,
    characterDataOldValue: true,
  });

  // 초기 타이머 시작
  resetTimer();
  console.log(`DOM 안정화 대기 시작 (${timeout}ms timeout)`);
};

/**
 * 특정 요소에 액션을 수행합니다.
 */
export const performElementAction = (
  selector: string | undefined, // sendKeys일 때는 옵셔널 (현재 포커스된 요소 사용)
  action: 'click' | 'focus' | 'scroll' | 'input' | 'doubleClick' | 'sendKeys',
  value?: string,
): boolean => {
  try {
    let element: HTMLElement | null = null;

    // sendKeys 액션이고 selector가 없으면 현재 포커스된 요소 사용
    if (action === 'sendKeys' && !selector) {
      element = document.activeElement as HTMLElement;
      if (!element) {
        console.warn('No focused element found for sendKeys action');
        return false;
      }
    } else if (selector) {
      element = document.querySelector(selector) as HTMLElement;
      if (!element) {
        console.warn(`Element not found with data-id: ${selector}`);
        return false;
      }
    } else {
      console.warn(`Selector is required for action: ${action}`);
      return false;
    }

    switch (action) {
      case 'click':
        element.click();
        break;
      case 'focus':
        element.focus();
        break;
      case 'scroll':
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;
      case 'input':
        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
          element.focus();

          if (value) {
            simulateTyping(element, value);
          }
        }
        break;
      case 'doubleClick':
        // 실제 더블클릭 시뮬레이션 (완전한 마우스 이벤트 시퀀스)
        simulateDoubleClick(element);
        break;
      case 'sendKeys':
        if (value) {
          simulateKeyPress(element, value);
        } else {
          console.warn('sendKeys action requires a value (e.g., "Enter", "Tab", etc.)');
        }
        break;
    }

    return true;
  } catch (error) {
    console.error(`Error performing action ${action} on data-id="${selector}":`, error);
    return false;
  }
};

function simulateTyping(element: HTMLInputElement | HTMLTextAreaElement, text: string) {
  // 포커스 설정
  element.focus();

  // React 호환성을 위한 native setter 사용
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value',
  )?.set;

  const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLTextAreaElement.prototype,
    'value',
  )?.set;

  // 기존 값 지우기 (React 방식)
  if (element instanceof HTMLInputElement && nativeInputValueSetter) {
    nativeInputValueSetter.call(element, '');
  } else if (element instanceof HTMLTextAreaElement && nativeTextAreaValueSetter) {
    nativeTextAreaValueSetter.call(element, '');
  }

  // input 이벤트로 빈 값 알림
  element.dispatchEvent(new Event('input', { bubbles: true }));

  // 새 값 설정 (React 방식)
  if (element instanceof HTMLInputElement && nativeInputValueSetter) {
    nativeInputValueSetter.call(element, text);
  } else if (element instanceof HTMLTextAreaElement && nativeTextAreaValueSetter) {
    nativeTextAreaValueSetter.call(element, text);
  }

  // 순차적으로 이벤트 발생 (시간 지연 포함)
  setTimeout(() => {
    // input 이벤트 (React onChange 감지)
    element.dispatchEvent(new Event('input', { bubbles: true }));

    setTimeout(() => {
      // change 이벤트
      element.dispatchEvent(new Event('change', { bubbles: true }));

      setTimeout(() => {
        // focusout 이벤트 (AG Grid에서 편집 완료 감지)
        element.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
      }, 10);
    }, 10);
  }, 10);
}

function simulateDoubleClick(element: HTMLElement) {
  // 요소의 중심점 계산
  const rect = element.getBoundingClientRect();
  const clientX = rect.left + rect.width / 2;
  const clientY = rect.top + rect.height / 2;

  const mouseEventOptions = {
    bubbles: true,
    cancelable: true,
    view: window,
    detail: 1,
    screenX: clientX,
    screenY: clientY,
    clientX: clientX,
    clientY: clientY,
    ctrlKey: false,
    altKey: false,
    shiftKey: false,
    metaKey: false,
    button: 0,
    relatedTarget: null,
  };

  // 첫 번째 클릭 시퀀스
  element.dispatchEvent(new MouseEvent('mousedown', mouseEventOptions));
  element.dispatchEvent(new MouseEvent('mouseup', mouseEventOptions));
  element.dispatchEvent(new MouseEvent('click', mouseEventOptions));

  // 짧은 지연 후 두 번째 클릭 시퀀스
  setTimeout(() => {
    const secondClickOptions = { ...mouseEventOptions, detail: 2 };

    element.dispatchEvent(new MouseEvent('mousedown', secondClickOptions));
    element.dispatchEvent(new MouseEvent('mouseup', secondClickOptions));
    element.dispatchEvent(new MouseEvent('click', secondClickOptions));

    // 마지막으로 dblclick 이벤트
    element.dispatchEvent(new MouseEvent('dblclick', secondClickOptions));
  }, 10); // 10ms 지연 (빠른 더블클릭)
}

function simulateKeyPress(element: HTMLElement, keyValue: string) {
  // 특수문자 매핑
  const specialKeys: {
    [key: string]: { key: string; keyCode: number; code: string; location?: number };
  } = {
    Enter: { key: 'Enter', keyCode: 13, code: 'Enter', location: 0 },
    Tab: { key: 'Tab', keyCode: 9, code: 'Tab', location: 0 },
    Escape: { key: 'Escape', keyCode: 27, code: 'Escape', location: 0 },
    Backspace: { key: 'Backspace', keyCode: 8, code: 'Backspace', location: 0 },
    Delete: { key: 'Delete', keyCode: 46, code: 'Delete', location: 0 },
    ArrowUp: { key: 'ArrowUp', keyCode: 38, code: 'ArrowUp', location: 0 },
    ArrowDown: { key: 'ArrowDown', keyCode: 40, code: 'ArrowDown', location: 0 },
    ArrowLeft: { key: 'ArrowLeft', keyCode: 37, code: 'ArrowLeft', location: 0 },
    ArrowRight: { key: 'ArrowRight', keyCode: 39, code: 'ArrowRight', location: 0 },
    Space: { key: ' ', keyCode: 32, code: 'Space', location: 0 },
  };

  const keyInfo = specialKeys[keyValue] || {
    key: keyValue,
    keyCode: keyValue.charCodeAt(0),
    code: `Key${keyValue.toUpperCase()}`,
    location: 0,
  };

  const keyEventOptions = {
    key: keyInfo.key,
    code: keyInfo.code,
    keyCode: keyInfo.keyCode,
    which: keyInfo.keyCode,
    location: keyInfo.location,
    bubbles: true,
    cancelable: true,
    composed: true,
  };

  // keydown 이벤트
  element.dispatchEvent(new KeyboardEvent('keydown', keyEventOptions));

  // keypress 이벤트 (특수키가 아닌 경우만)
  setTimeout(() => {
    if (!specialKeys[keyValue] && keyValue.length === 1) {
      element.dispatchEvent(new KeyboardEvent('keypress', keyEventOptions));
    }

    // keyup 이벤트
    setTimeout(() => {
      element.dispatchEvent(new KeyboardEvent('keyup', keyEventOptions));

      // 특수 키별 후처리
      handleSpecialKeyActions(element, keyValue);
    }, 5); // 5ms 후 keyup
  }, 5); // 5ms 후 keypress
}

function handleSpecialKeyActions(element: HTMLElement, keyValue: string) {
  switch (keyValue) {
    case 'Enter':
      setTimeout(() => {
        // AG Grid나 React 컴포넌트를 위한 추가 이벤트들
        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
          // change 이벤트 발생
          element.dispatchEvent(new Event('change', { bubbles: true }));

          setTimeout(() => {
            // focusout 이벤트로 편집 완료 신호 (AG Grid용)
            element.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));

            // blur 이벤트로 편집 종료
            element.dispatchEvent(new FocusEvent('blur', { bubbles: true }));
          }, 10);
        }
      }, 10);
      break;

    case 'Tab':
      setTimeout(() => {
        // Tab 키는 포커스 이동을 시뮬레이션
        element.dispatchEvent(new FocusEvent('blur', { bubbles: true }));
      }, 10);
      break;

    case 'Escape':
      setTimeout(() => {
        // Escape는 편집 취소를 시뮬레이션
        element.dispatchEvent(new FocusEvent('blur', { bubbles: true }));
      }, 10);
      break;

    default:
      console.log(`Key "${keyValue}" simulation completed`);
  }
}
